import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

export default function ViewRadiologistStatus({ onCancel, onSave }) {
     const { candidateId } = useParams();
     const navigate = useNavigate();
     const baseUrl = process.env.REACT_APP_BASE_URL;

     const [modalities, setModalities] = useState([]);
     const [isSubmitting, setIsSubmitting] = useState(false);
     const [loading, setLoading] = useState(false);
     const [fetchError, setFetchError] = useState(null);
     const [regions, setRegions] = useState([]);

     const [personal, setPersonal] = useState({
          positionApplied: "",
          candidateName: "",
          mobile: "",
          email: "",
          Paediatric: "",
          education_qualification: "",
          total_experience: "",
          rptMgrStatus: "",
          overallNotes: "",
          managerApprovedDate: "",
          timingsFrom: "",
          timingsTo: "",
          hrStatus: "",
          hrApprovedDate: "",
     });

     const [personality, setPersonality] = useState([]);
     const [technical, setTechnical] = useState([]);

     // Fetch candidate details from API
     useEffect(() => {
          if (!candidateId) return;

          setLoading(true);
          setFetchError(null);

          fetch(`${baseUrl}/getCandidateFullDetails/${candidateId}`, {
               method: "GET",
               headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${sessionStorage.getItem("token")}`,
               },
          })
               .then((res) => {
                    if (!res.ok) throw new Error("Failed to fetch candidate details");
                    return res.json();
               })
               .then((data) => {
                    const candidate = data.candidate || {};

                    setPersonal({
                         positionApplied: candidate.position_applied || "",
                         candidateName: candidate.candidate_name || "",
                         mobile: candidate.mobile || "",
                         email: candidate.email || "",
                         Paediatric: candidate.Paediatric || "",
                         education_qualification: candidate.education_qualification || "",
                         total_experience: candidate.total_experience || "",
                         timingsFrom: candidate.timing_from || "",
                         timingsTo: candidate.timing_to || "",
                         rptMgrStatus: candidate.rpt_mgr_status || "",
                         overallNotes: candidate.overallNotes || "",
                         managerApprovedDate: candidate.manager_approved_date || "",
                         hrStatus: candidate.hr_status || "",
                         hrApprovedDate: candidate.hr_approved_date || "",
                         medical_registration: candidate.medical_registration || "",
                         resume_file: candidate.resume_file || ""
                    });

                    setModalities(
                         (data.modalities || []).map((mod) => ({
                              modality: mod.modality || "",
                              submodality: mod.selection_area || "",
                              capability: mod.level || "",
                              applicable: mod.applicable || "",
                         }))
                    );

                    setPersonality(data.personality_assessments || []);
                    setRegions(data.regions || []);
                    setTechnical(data.technical_evaluations || []);

                    setLoading(false);
               })
               .catch((error) => {
                    setLoading(false);
                    setFetchError(error.message);
               });
     }, [candidateId]);

     if (loading) return <div>Loading candidate details...</div>;
     if (fetchError) return <div style={{ color: "red" }}>Error: {fetchError}</div>;

     const styles = {
          form: {
               maxWidth: 900,
               margin: "20px auto",
               padding: 30,
               background: "#fff",
               borderRadius: 8,
               boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
               fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
               color: "#333",
          },
          section: {
               marginBottom: 30,
          },
          sectionHeader: {
               borderBottom: "2px solid #007bff",
               paddingBottom: 8,
               marginBottom: 20,
               color: "#007bff",
          },
          formGrid: {
               display: "grid",
               gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
               gap: "20px 30px",
          },
          label: {
               display: "flex",
               flexDirection: "column",
               fontWeight: 600,
               fontSize: 14,
               color: "#444",
          },
          input: {
               marginTop: 6,
               padding: "10px 12px",
               fontSize: 14,
               border: "1.8px solid #ccc",
               borderRadius: 6,
               backgroundColor: "#f9f9f9",
               cursor: "not-allowed",
          },
          textarea: {
               marginTop: 6,
               padding: "10px 12px",
               fontSize: 14,
               border: "1.8px solid #ccc",
               borderRadius: 6,
               backgroundColor: "#f9f9f9",
               cursor: "not-allowed",
               resize: "vertical",
          },
          table: {
               width: "100%",
               borderCollapse: "collapse",
               fontSize: 14,
          },
          th: {
               border: "1px solid #ddd",
               padding: "12px 15px",
               textAlign: "left",
               backgroundColor: "#007bff",
               color: "white",
               fontWeight: 600,
          },
          td: {
               border: "1px solid #ddd",
               padding: "12px 15px",
               textAlign: "left",
          },
          tbodyEvenRow: {
               backgroundColor: "#f7f7f7",
          },
          formActions: {
               display: "flex",
               justifyContent: "flex-end",
               gap: 15,
          },
          btnCancel: {
               padding: "10px 22px",
               borderRadius: 6,
               fontWeight: 600,
               fontSize: 15,
               cursor: "pointer",
               border: "none",
               backgroundColor: "#ddd",
               color: "#444",
          },
          btnSubmit: {
               padding: "10px 22px",
               borderRadius: 6,
               fontWeight: 600,
               fontSize: 15,
               cursor: "pointer",
               border: "none",
               backgroundColor: "#007bff",
               color: "white",
          },
          btnSubmitDisabled: {
               backgroundColor: "#5a9bf6",
               cursor: "not-allowed",
          },
     };

     const compactInputStyle = {
          padding: "4px 8px",
          fontSize: "13px",
          lineHeight: "1.2",
          height: "28px",
          border: "1px solid #ccc",
          borderRadius: "3px",
          boxSizing: "border-box",
     };

     const handlePersonalChange = (e) => {
          const { name, value } = e.target;
          setPersonal((prev) => ({ ...prev, [name]: value }));
     };

     return (
          <form onSubmit={(e) => e.preventDefault()} style={styles.form}>
               {/* Personal Details */}
               <section style={styles.section}>
                    <h2 style={styles.sectionHeader}>Personal Details</h2>
                    <div style={styles.formGrid}>
                         <label style={styles.label}>
                              Position Applied
                              <input
                                   name="positionApplied"
                                   value={personal.positionApplied}
                                   readOnly
                                   style={{ ...styles.input, ...compactInputStyle }}
                              />
                         </label>
                         <label style={styles.label}>
                              Candidate Name
                              <input
                                   name="candidateName"
                                   value={personal.candidateName}
                                   readOnly
                                   style={{ ...styles.input, ...compactInputStyle }}
                              />
                         </label>
                         <label style={styles.label}>
                              Mobile
                              <input
                                   name="mobile"
                                   value={personal.mobile}
                                   readOnly
                                   style={{ ...styles.input, ...compactInputStyle }}
                              />
                         </label>
                         <label style={styles.label}>
                              Email
                              <input
                                   name="email"
                                   value={personal.email}
                                   readOnly
                                   style={{ ...styles.input, ...compactInputStyle }}
                              />
                         </label>
                         <label style={styles.label}>
                              Paediatric
                              <select
                                   name="Paediatric"
                                   value={personal.Paediatric}
                                   onChange={handlePersonalChange}
                                   style={{ ...styles.input, ...compactInputStyle }}
                                   disabled
                              >
                                   <option value="">-- Select --</option>
                                   <option value="Y">Yes</option>
                                   <option value="N">No</option>
                              </select>
                         </label>
                         <label style={styles.label}>
                              Medical Registration
                              <select
                                   name="medical_registration"
                                   value={personal.medical_registration}
                                   onChange={handlePersonalChange}
                                   style={{ ...styles.input, ...compactInputStyle }}
                                   disabled
                              >
                                   <option value="">-- Select --</option>
                                   <option value="Y">Yes</option>
                                   <option value="N">No</option>
                              </select>
                         </label>
                         <label style={styles.label}>
                              Qualification
                              <input
                                   name="education_qualification"
                                   value={personal.education_qualification}
                                   onChange={handlePersonalChange}
                                   style={{ ...styles.input, ...compactInputStyle }}
                              />
                         </label>
                         <label style={styles.label}>
                              Total Experience
                              <input
                                   name="total_experience"
                                   value={personal.total_experience}
                                   onChange={handlePersonalChange}
                                   style={{ ...styles.input, ...compactInputStyle }}
                              />
                         </label>
                         <label>
                              Timings From
                              <input
                                   disabled
                                   required
                                   type="time"
                                   className="form-control input-form"
                                   name="timingsFrom"
                                   min="07:00"
                                   max="23:00"
                                   value={personal.timingsFrom}
                                   onChange={handlePersonalChange}

                              />

                         </label>

                         <label>
                              Timings To
                              <input
                                   disabled
                                   required
                                   type="time"
                                   min="07:00"
                                   max="23:00"
                                   className="form-control input-form"
                                   name="timingsTo"
                                   value={personal.timingsTo}
                                   onChange={handlePersonalChange}
                              />

                         </label>
                         <label style={styles.label}>
                              Reporting Manager Status
                              <input
                                   value={personal.rptMgrStatus}
                                   readOnly
                                   style={{ ...styles.input, ...compactInputStyle }}
                              />
                         </label>
                         <label style={styles.label}>
                              Manager Approved Date
                              <input
                                   value={personal.managerApprovedDate}
                                   readOnly
                                   style={{ ...styles.input, ...compactInputStyle }}
                              />
                         </label>
                         {/* <label style={styles.label}>
            HR Status
            <input value={personal.hrStatus} readOnly style={{ ...styles.input, ...compactInputStyle }} />
          </label>
          <label style={styles.label}>
            HR Approved Date
            <input
              value={personal.hrApprovedDate}
              readOnly
              style={{ ...styles.input, ...compactInputStyle }}
            />
          </label> */}
                         <label style={styles.label}>
                              Overall Notes
                              <textarea
                                   value={personal.overallNotes}
                                   readOnly
                                   rows={3}
                                   style={styles.textarea}
                              />
                         </label>

                         {personal.resume_file && (
                              <div style={{ marginTop: "6px" }}>
                                   <a
                                        href={`${process.env.REACT_APP_FILE_URL}/uploads/candidate_resume/${personal.resume_file}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                             color: "#007bff",
                                             textDecoration: "underline"
                                        }}
                                   >
                                        View Uploaded Image
                                   </a>

                                   {/* Optional thumbnail preview for images */}
                                   {personal.resume_file.match(/\.(jpg|jpeg|png|gif)$/i) && (
                                        <img
                                             src={`${process.env.REACT_APP_FILE_URL}/uploads/candidate_resume/${personal.resume_file}`}
                                             alt="Resume"
                                             style={{
                                                  display: "block",
                                                  marginTop: "8px",
                                                  width: "120px",
                                                  height: "auto",
                                                  border: "1px solid #ddd",
                                                  borderRadius: "4px"
                                             }}
                                        />
                                   )}
                              </div>
                         )}
                    </div>
               </section>

               {/* Modalities Table */}
               <section style={styles.section}>
                    <h2 style={styles.sectionHeader}>Modalities</h2>
                    <table style={styles.table}>
                         <thead>
                              <tr>
                                   <th style={styles.th}>Modality</th>
                                   <th style={styles.th}>Selection Area (Submodality)</th>
                                   <th style={styles.th}>Level (Capability)</th>
                                   <th style={styles.th}>Applicable (Capability)</th>
                              </tr>
                         </thead>
                         <tbody>
                              {modalities
                                   .filter((mod) => mod.applicable === "YES")
                                   .map((mod, idx) => (
                                        <tr
                                             key={idx}
                                             style={idx % 2 === 1 ? styles.tbodyEvenRow : undefined}
                                        >
                                             <td style={styles.td}>{mod.modality}</td>
                                             <td style={styles.td}>{mod.submodality}</td>
                                             <td style={styles.td}>{mod.capability}</td>
                                             <td style={styles.td}>{mod.applicable}</td>
                                        </tr>
                                   ))}
                         </tbody>
                    </table>
               </section>

               {/* Personality Assessments */}
               {/* Technical Evaluations */}
               <section style={styles.section}>
                    <h2 style={styles.sectionHeader}>Technical Evaluations</h2>
                    <table style={styles.table}>
                         <thead>
                              <tr>
                                   <th style={styles.th}>Particular</th>
                                   <th style={styles.th}>Assessment</th>
                                   <th style={styles.th}>Notes</th>
                                   <th style={styles.th}>Grading</th>
                              </tr>
                         </thead>
                         <tbody>
                              {technical.map((item, idx) => (
                                   <tr
                                        key={item.id}
                                        style={idx % 2 === 1 ? styles.tbodyEvenRow : undefined}
                                   >
                                        <td style={styles.td}>{item.particular}</td>
                                        <td style={styles.td}>{item.assessment}</td>
                                        <td style={styles.td}>{item.notes}</td>
                                        <td style={styles.td}>{item.grading}</td>
                                   </tr>
                              ))}
                         </tbody>
                    </table>
               </section>

               {/* Regions */}
               <section style={styles.section}>
                    <h2 style={styles.sectionHeader}>Regions</h2>
                    <table style={styles.table}>
                         <thead>
                              <tr>
                                   <th style={styles.th}>Region Name</th>
                                   <th style={styles.th}>Branch Name</th>
                              </tr>
                         </thead>
                         <tbody>
                              {regions.map((item, idx) => (
                                   <tr
                                        key={item.id}
                                        style={idx % 2 === 1 ? styles.tbodyEvenRow : undefined}
                                   >
                                        <td style={styles.td}>{item.region_name}</td>
                                        <td style={styles.td}>{item.branch_name}</td>
                                   </tr>
                              ))}
                         </tbody>
                    </table>
               </section>

               {personality.filter(item => item.remarks && item.remarks.trim() !== "").length > 0 && (
                    <section style={styles.section}>
                         <h2 style={styles.sectionHeader}>Personality Assessments</h2>
                         <table style={styles.table}>
                              <thead>
                                   <tr>
                                        <th style={styles.th}>Criteria</th>
                                        <th style={styles.th}>Remarks</th>
                                   </tr>
                              </thead>
                              <tbody>
                                   {personality
                                        .filter(item => item.remarks && item.remarks.trim() !== "")
                                        .map((item, idx) => (
                                             <tr
                                                  key={item.id}
                                                  style={idx % 2 === 1 ? styles.tbodyEvenRow : undefined}
                                             >
                                                  <td style={styles.td}>{item.criteria}</td>
                                                  <td style={styles.td}>{item.remarks}</td>
                                             </tr>
                                        ))}
                              </tbody>
                         </table>
                    </section>
               )}


          </form>
     );
}
